import { useEffect, useRef } from 'react';
import { ReturnItem, ReturnSlip } from 'returnCenter-api';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { returnSelectors, setBatchHandled } from 'store/reducers/returnSlice';

/**
 * A row that can go into a batch: eligible for a destination and actually being returned. Split rows
 * are rows of their own, inherit the destination but not the batch, so each needs its own assignment.
 */
export const isBatchAssignable = (item: ReturnItem) =>
    item.batchReturnDestinationId != null && (item.quantityInReturn ?? 0) > 0;

/** Null when the slip has nothing that can go into a batch. */
export const allBatchAssignableRowsAssigned = (slip: ReturnSlip | null | undefined): boolean | null => {
    let assignable = 0;
    for (const group of slip?.returnItems ?? []) {
        for (const line of group.orderLines ?? []) {
            for (const item of line.returnItems ?? []) {
                if (!isBatchAssignable(item)) continue;
                assignable++;
                if (!item.batchReturnId) return false;
            }
        }
    }
    return assignable > 0 ? true : null;
};

/**
 * Approves batch handling as soon as the last assignable row gets a batch, and withdraws that
 * approval if a row then loses its batch or a new split row appears without one. Only reacts to
 * changes made while the slip is open: a slip opened in either state is left as it was saved, and
 * an approval or unapproval made by hand is never overridden.
 */
const useAutoApproveBatchHandling = () => {
    const dispatch = useAppDispatch();
    const slip = useAppSelector(returnSelectors.selectReturnSlip);
    const tracked = useRef<{
        started: boolean;
        slipId: number | null | undefined;
        allAssigned: boolean | null;
        autoApproved: boolean;
    }>({ started: false, slipId: undefined, allAssigned: null, autoApproved: false });

    useEffect(() => {
        if (!slip) return;

        const allAssigned = allBatchAssignableRowsAssigned(slip);
        const state = tracked.current;

        if (!state.started || state.slipId !== slip.id) {
            tracked.current = { started: true, slipId: slip.id, allAssigned, autoApproved: false };
            return;
        }

        if (allAssigned === state.allAssigned) return;
        state.allAssigned = allAssigned;

        if (allAssigned && !slip.batchHandled) {
            dispatch(setBatchHandled(true));
            state.autoApproved = true;
        } else if (!allAssigned && state.autoApproved) {
            if (slip.batchHandled) dispatch(setBatchHandled(false));
            state.autoApproved = false;
        }
    }, [dispatch, slip]);

    // A hand-made unapproval ends the automatic one; the next completed assignment approves again.
    const batchHandled = slip?.batchHandled;
    useEffect(() => {
        if (batchHandled === false) tracked.current.autoApproved = false;
    }, [batchHandled]);
};

export default useAutoApproveBatchHandling;
